import {
  DUEL_LEVELS,
  duelLevelSkill,
  isDuelLevel,
  type ArenaAiSkill,
  type BoardDef,
  type DuelLevel,
  type FactionId,
  type ServerMessage,
} from '@archon3d/core';
import { sameDevice, type SeatDevice } from '../game/controls';

export { DUEL_LEVELS, type DuelLevel };
export type { SeatDevice };

/** Who controls a side: a person at this device, the AI, or nobody (the side sits out). */
export type SeatKind = 'human' | 'ai' | 'none';

export interface SeatSetup {
  kind: SeatKind;
  /** A person's name, shown on the sign and in the lobby. */
  name?: string;
  /**
   * What this person plays with when several people sit at this computer with a controller each
   * (`oneDevice` false): the mouse and keyboard, or one particular gamepad. Absent when the people
   * share the device or when one person plays, since then every device is theirs.
   */
  device?: SeatDevice;
  /** Plies the board AI looks ahead for this seat, 1 … 4. */
  depth?: number;
  /** How well this seat's pieces fight in the arena when the AI drives them. */
  duel?: DuelLevel;
}

/**
 * How captures are settled, in the order the setup offers them: on the card board, by a seeded
 * roll, fought in the arena by the people at the table, or watched while the AI fights both sides.
 * Card is the default for a new game.
 */
export type FightsMode = 'card' | 'roll' | 'arena' | 'watch';
export const FIGHTS_MODES: readonly FightsMode[] = ['card', 'roll', 'arena', 'watch'];
export const DEFAULT_FIGHTS: FightsMode = 'card';

/**
 * Who sits where for the next game: the board, a seat per side in board order, and how the game
 * runs. Sides marked `none` sit out (any side may, not only the last ones); at least two play.
 */
export interface GameSetup {
  boardId: string;
  seats: SeatSetup[];
  /** Several people share this device and take turns; the AI fights every duel for them. */
  oneDevice: boolean;
  fights: FightsMode;
  /** Gentler Arena AI only when fighting a person; missing older values default on. */
  humanFriendlyAi?: boolean;
  /** Immediate duel, or a Join/Pass window for other reachable factions. */
  engagement?: 'immediate' | 'muster';
  fog: boolean;
}

/** The ready-made setups the table's tablet offers with one tap. */
export type SetupPreset = 'light-vs-ai' | 'dark-vs-ai' | 'hot-seat' | 'ai-vs-ai';

export const SETUP_PRESETS: { id: SetupPreset; label: string }[] = [
  { id: 'light-vs-ai', label: 'Light vs AI' },
  { id: 'dark-vs-ai', label: 'Dark vs AI' },
  { id: 'hot-seat', label: 'Two players' },
  { id: 'ai-vs-ai', label: 'Watch AI' },
];

export const MAX_PLAYERS = 6;
export const DEFAULT_DEPTH = 2;
export const DEFAULT_DUEL: DuelLevel = 'fair';

export function aiSeat(depth = DEFAULT_DEPTH, duel: DuelLevel = DEFAULT_DUEL): SeatSetup {
  return { kind: 'ai', depth, duel };
}

export function humanSeat(name?: string, device?: SeatDevice): SeatSetup {
  const seat: SeatSetup = { kind: 'human' };
  if (name) seat.name = name;
  if (device) seat.device = device;
  return seat;
}

/** Several people at this computer, each with a controller of their own (not taking turns). */
export function controllersEach(setup: GameSetup): boolean {
  return setup.seats.filter((s) => s.kind === 'human').length > 1 && !setup.oneDevice;
}

/** Every person who needs a controller of their own has chosen one. */
export function controlsComplete(setup: GameSetup): boolean {
  return (
    !controllersEach(setup) ||
    setup.seats.every((s) => s.kind !== 'human' || s.device !== undefined)
  );
}

/** A local game with the first side human and every remaining side a fair AI. */
/**
 * The setup a new game starts from: the first seat a person, the others AIs thinking `depth`
 * plies ahead, the Settings' own choice (client-tablets-ui:quality#3).
 */
export function defaultSetup(board: BoardDef, depth = DEFAULT_DEPTH): GameSetup {
  return {
    boardId: board.id,
    seats: board.factions.map((_, i) => (i === 0 ? humanSeat() : aiSeat(depth))),
    oneDevice: false,
    fights: DEFAULT_FIGHTS,
    humanFriendlyAi: true,
    engagement: 'immediate',
    fog: true,
  };
}

/** A preset applied to a board: the first two seats follow the preset, the others sit out. */
export function presetSetup(board: BoardDef, preset: SetupPreset, from?: GameSetup): GameSetup {
  const seats: SeatSetup[] = board.factions.map((_, i) => {
    if (i >= 2) return { kind: 'none' };
    const human =
      preset === 'hot-seat' ||
      (preset === 'light-vs-ai' && i === 0) ||
      (preset === 'dark-vs-ai' && i === 1);
    return human ? humanSeat() : aiSeat(from?.seats[i]?.depth, from?.seats[i]?.duel);
  });
  const humans = seats.filter((s) => s.kind === 'human').length;
  return {
    boardId: board.id,
    seats,
    oneDevice: humans > 1,
    fights: DEFAULT_FIGHTS,
    humanFriendlyAi: from?.humanFriendlyAi ?? true,
    engagement: 'immediate',
    fog: from?.fog ?? true,
  };
}

/** Which preset a setup amounts to, if any (so the tablet can mark it as chosen). */
export function presetOf(setup: GameSetup): SetupPreset | null {
  const kinds = setup.seats.map((s) => s.kind);
  if (kinds.filter((k) => k !== 'none').length !== 2 || kinds.slice(2).some((k) => k !== 'none'))
    return null;
  const [a, b] = kinds;
  if (a === 'human' && b === 'human') return 'hot-seat';
  if (a === 'human' && b === 'ai') return 'light-vs-ai';
  if (a === 'ai' && b === 'human') return 'dark-vs-ai';
  if (a === 'ai' && b === 'ai') return 'ai-vs-ai';
  return null;
}

/** Brings a setup in line with a board and its own rules after either changed. */
export function fitSetup(setup: GameSetup, board: BoardDef): GameSetup {
  const seats: SeatSetup[] = board.factions.map((_, i) => {
    const seat = setup.seats[i] ?? (i < 2 ? aiSeat() : { kind: 'none' });
    if (seat.kind === 'ai')
      return {
        kind: 'ai',
        depth: Math.max(1, Math.min(4, Math.round(seat.depth ?? DEFAULT_DEPTH))),
        duel: isDuelLevel(seat.duel) ? seat.duel : DEFAULT_DUEL,
      };
    if (seat.kind === 'human') return humanSeat(seat.name, seat.device);
    return { kind: 'none' };
  });
  // At least two sides play: bring the first ones that sit out back as AI.
  for (let i = 0; i < seats.length && seats.filter((s) => s.kind !== 'none').length < 2; i++)
    if (seats[i]!.kind === 'none') seats[i] = aiSeat();
  // No more than the table can seat.
  let playing = seats.filter((s) => s.kind !== 'none').length;
  for (let i = seats.length - 1; i >= 0 && playing > MAX_PLAYERS; i--)
    if (seats[i]!.kind !== 'none') {
      seats[i] = { kind: 'none' };
      playing -= 1;
    }
  const humans = seats.filter((s) => s.kind === 'human').length;
  const oneDevice = humans > 1 && setup.oneDevice;
  // A controller each only when several people play without sharing; and one person per device:
  // the mouse to one seat at most, a pad to the first seat that chose it.
  const taken: SeatDevice[] = [];
  for (const seat of seats) {
    if (seat.kind !== 'human' || !seat.device) continue;
    if (humans < 2 || oneDevice || taken.some((d) => sameDevice(d, seat.device)))
      delete seat.device;
    else taken.push(seat.device);
  }
  const fights: FightsMode =
    setup.fights === 'arena' && (humans === 0 || oneDevice) ? 'watch' : setup.fights;
  const engagement =
    fights === 'arena' || fights === 'watch'
      ? 'immediate'
      : (setup.engagement ?? (board.grid === 'hex' ? 'muster' : 'immediate'));
  return {
    ...setup,
    boardId: board.id,
    seats,
    oneDevice,
    fights,
    engagement,
    humanFriendlyAi: setup.humanFriendlyAi ?? true,
  };
}

/** Sides that play, by faction id, in board order. */
export function playingFactions(setup: GameSetup, board: BoardDef): FactionId[] {
  return board.factions.filter((_, i) => setup.seats[i]?.kind !== 'none').map((f) => f.id);
}

/** The seat map the session wants: faction id → who plays it (sides that sit out are absent). */
export function seatsByFaction(
  setup: GameSetup,
  board: BoardDef,
): Record<FactionId, 'human' | 'ai'> {
  const out: Record<FactionId, 'human' | 'ai'> = {};
  board.factions.forEach((f, i) => {
    const seat = setup.seats[i];
    if (seat && seat.kind !== 'none') out[f.id] = seat.kind;
  });
  return out;
}

/** The AI's strength per AI seat: search depth and arena skill. */
export function aiByFaction(
  setup: GameSetup,
  board: BoardDef,
): Record<FactionId, { depth: number; skill: ArenaAiSkill }> {
  const out: Record<FactionId, { depth: number; skill: ArenaAiSkill }> = {};
  board.factions.forEach((f, i) => {
    const seat = setup.seats[i];
    if (seat?.kind === 'ai')
      out[f.id] = { depth: seat.depth ?? DEFAULT_DEPTH, skill: duelLevelSkill(seat.duel) };
  });
  return out;
}

/** People's names per faction, for the sign and the lobby; empty where nobody named themselves. */
export function namesByFaction(setup: GameSetup, board: BoardDef): Record<FactionId, string> {
  const out: Record<FactionId, string> = {};
  board.factions.forEach((f, i) => {
    const seat = setup.seats[i];
    if (seat?.kind === 'human' && seat.name) out[f.id] = seat.name;
  });
  return out;
}

/**
 * Each person's controller per faction, when the people at this computer have one each; absent
 * when they share the device or one person plays, and every device then drives whoever is on.
 */
export function devicesByFaction(
  setup: GameSetup,
  board: BoardDef,
): Record<FactionId, SeatDevice> | undefined {
  if (!controllersEach(setup)) return undefined;
  const out: Record<FactionId, SeatDevice> = {};
  board.factions.forEach((f, i) => {
    const seat = setup.seats[i];
    if (seat?.kind === 'human' && seat.device) out[f.id] = seat.device;
  });
  return out;
}

/** "Light vs AI", "Anna and Bo at one table", "AI against AI" … for headings and the tablet. */
export function describeSetup(setup: GameSetup, board: BoardDef): string {
  const playing = board.factions.filter((_, i) => setup.seats[i]?.kind !== 'none');
  const label = (f: BoardDef['factions'][number], i: number) => setup.seats[i]?.name || f.name;
  const humans = board.factions
    .map((f, i) => ({ f, i }))
    .filter(({ i }) => setup.seats[i]?.kind === 'human')
    .map(({ f, i }) => label(f, i));
  if (humans.length === 0) return 'AI against AI';
  if (humans.length === playing.length) return `${andList(humans)} at one table`;
  const ais = playing.length - humans.length;
  return `${andList(humans)} vs ${ais === 1 ? 'AI' : `${ais} AIs`}`;
}

function andList(names: string[]): string {
  return names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** Reads a setup stored by this or an earlier build (which had `players` and plain seat strings). */
export function migrateSetup(raw: unknown, board: BoardDef): GameSetup | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as {
    boardId?: unknown;
    seats?: unknown;
    players?: unknown;
    oneDevice?: unknown;
    fights?: unknown;
    humanFriendlyAi?: unknown;
    fog?: unknown;
    engagement?: unknown;
  };
  if (typeof r.boardId !== 'string' || !Array.isArray(r.seats)) return null;
  const old = typeof r.players === 'number';
  const seats: SeatSetup[] = r.seats.map((s: unknown, i): SeatSetup => {
    if (typeof s === 'string')
      return old && i >= (r.players as number)
        ? { kind: 'none' }
        : s === 'human'
          ? humanSeat()
          : aiSeat();
    if (s && typeof s === 'object') {
      const o = s as Partial<SeatSetup>;
      if (o.kind === 'human')
        return humanSeat(typeof o.name === 'string' ? o.name : undefined, readDevice(o.device));
      if (o.kind === 'ai')
        return aiSeat(
          typeof o.depth === 'number' ? o.depth : undefined,
          isDuelLevel(o.duel) ? o.duel : undefined,
        );
    }
    return { kind: 'none' };
  });
  return fitSetup(
    {
      boardId: r.boardId,
      seats,
      oneDevice: r.oneDevice === true,
      humanFriendlyAi: typeof r.humanFriendlyAi === 'boolean' ? r.humanFriendlyAi : true,
      fights:
        r.fights === 'arena' || r.fights === 'card' || r.fights === 'roll' || r.fights === 'watch'
          ? r.fights
          : 'card',
      engagement: r.engagement === 'muster' ? 'muster' : 'immediate',
      fog: r.fog === true,
    },
    board,
  );
}

/** A stored controller choice, if it still has the shape of one. */
function readDevice(raw: unknown): SeatDevice | undefined {
  if (raw === 'mouse') return 'mouse';
  if (raw && typeof raw === 'object') {
    const o = raw as { pad?: unknown; id?: unknown };
    if (Number.isInteger(o.pad) && (o.pad as number) >= 0 && typeof o.id === 'string')
      return { pad: o.pad as number, id: o.id };
  }
  return undefined;
}

/**
 * A hosted game's setup as the tablets read it: who sat where when it began (a person, the AI or
 * nobody), each AI seat with the levels its host gave it, and the room's rules. A room's game is
 * not the menus' draft, which is what the tablets showed for it before.
 */
export function roomSetup(
  started: Extract<ServerMessage, { t: 'started' }>,
  fog: boolean,
): GameSetup {
  const lobby = started.setup;
  return {
    boardId: lobby.boardId,
    seats: started.seats.map((kind, i) => {
      const room = kind === 'ai' ? lobby.seats?.[i] : undefined;
      return {
        kind,
        ...(started.names[i] ? { name: started.names[i] } : {}),
        ...(room?.depth !== undefined ? { depth: room.depth } : {}),
        ...(room?.duel !== undefined ? { duel: room.duel } : {}),
      };
    }),
    oneDevice: false,
    fights: lobby.fights ?? DEFAULT_FIGHTS,
    ...(lobby.humanFriendlyAi !== undefined ? { humanFriendlyAi: lobby.humanFriendlyAi } : {}),
    ...(lobby.engagement ? { engagement: lobby.engagement } : {}),
    fog,
  };
}
